"use client";
import { useMemo } from "react";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { ChevronRight, ChevronLeft } from "lucide-react";
import { cn } from "@/lib/cn";
import { useT } from "@/i18n/provider";
import { sectionTrail, isSectionLanding } from "@/lib/breadcrumbs";
import { useReturnContext, returnCrumbs } from "@/lib/workflow";
import type { BreadcrumbCrumb } from "@/components/workflow";

export type { BreadcrumbCrumb };

/**
 * Return-aware trail for pages reached from a project ("fix the connection"
 * links carry `?from=project&projectId=…&projectName=…`). When that context is
 * present the trail reads `Projects › <name> › <leaf>` — where the operator
 * actually came from — and the back-chevron returns to the project instead of
 * pretending they navigated down the settings tree. Without the context this
 * returns undefined and the caller falls back to the route-derived trail.
 *
 * The params are display/navigation hints only (the target link is always the
 * tenant-scoped project page, which authorises server-side), so they need no
 * validation beyond a sanity cap on the label.
 */
export function useProjectReturnCrumbs(leaf: string): BreadcrumbCrumb[] | undefined {
  const t = useT();
  const searchParams = useSearchParams();
  const from = searchParams?.get("from");
  const projectId = searchParams?.get("projectId");
  const projectName = searchParams?.get("projectName");
  return useMemo(() => {
    if (from !== "project" || !projectId) return undefined;
    return [
      { label: t("nav.projects"), href: "/projects" },
      {
        label: (projectName ?? "").slice(0, 80) || t("nav.projects"),
        href: `/projects/${encodeURIComponent(projectId)}`,
      },
      { label: leaf },
    ];
  }, [from, projectId, projectName, leaf, t]);
}

/**
 * The resolved trail for the current route, in the same two modes the bar
 * itself supports (explicit `items`, or derived from the pathname).
 *
 * Extracted from `<Breadcrumbs>` so chrome that WRAPS the bar — `<PageBar>`
 * needs to know whether there is a trail at all before it commits to
 * rendering a sticky strip around it — can ask the same question without
 * rendering, and without re-deriving the rules a second time.
 */
export function useBreadcrumbTrail({
  items,
  leaf,
}: {
  items?: BreadcrumbCrumb[];
  leaf?: string;
}): BreadcrumbCrumb[] {
  const t = useT();
  const pathname = usePathname() ?? "";
  // Where the operator actually came from, when the inbound link said so.
  const { context } = useReturnContext();

  return useMemo<BreadcrumbCrumb[]>(() => {
    // Explicit trail — entity detail pages own their chain (incl. the
    // return-context head), so render it verbatim.
    if (items && items.length) return items;

    // A return context REPLACES the route-derived root: a mission opened from
    // a project came from `Projects › Acme › Missions`, and the taxonomy trail
    // ("AI missions") would send the operator somewhere they have never been.
    if (context) {
      const out: BreadcrumbCrumb[] = returnCrumbs(context).map((c) => ({ label: c.label, href: c.href }));
      if (leaf) out.push({ label: leaf });
      if (out.length) out[out.length - 1] = { ...out[out.length - 1], href: undefined };
      return out;
    }

    // Route-derived section trail.
    const trail = sectionTrail(pathname);
    const out: BreadcrumbCrumb[] = trail.map((s) => ({ label: t(s.labelKey), href: s.href }));
    const deepest = trail[trail.length - 1];
    const onLanding = deepest ? isSectionLanding(pathname, deepest) : false;
    // Append the current page as a leaf only when the route goes deeper
    // than the deepest known section (a sub-page or detail entity).
    if (leaf && !onLanding) out.push({ label: leaf });
    // The last crumb is always the current page — strip its link.
    if (out.length) out[out.length - 1] = { ...out[out.length - 1], href: undefined };
    return out;
  }, [items, leaf, pathname, t, context]);
}

/**
 * Prominent, navigable breadcrumb bar — the "back-row bar" idiom: a
 * leading ‹ chevron that jumps one step back, the clickable trail, and
 * a hairline that separates it from the page title below.
 *
 * Two modes:
 *
 *   - **Derived** (default): the trail is computed from the route via
 *     `sectionTrail` (see `lib/breadcrumbs`). The page title is appended
 *     as the current (non-clickable) leaf. This is what every page gets
 *     for free through `PageHeader`, with locale-correct, clickable
 *     section roots.
 *
 *   - **Explicit** (`items`): entity detail pages pass their own
 *     return-aware chain (e.g. `Inbox › BIZ-12 › Patch v3`), where the
 *     leftmost crumb reflects where the operator actually came from
 *     (`?from=…`). The last crumb (no `href`) is treated as current.
 *
 * The bar renders on every page with a resolvable trail — including a
 * lone top-level crumb on a section landing page, which renders without
 * a back chevron (just the current location). Only a genuinely empty
 * trail falls back to the plain `eyebrow` line.
 */
export function Breadcrumbs({
  items,
  leaf,
  eyebrow,
  divider = true,
  className,
}: {
  /** Explicit full trail. When omitted, derived from the route. */
  items?: BreadcrumbCrumb[];
  /** Current-page label appended in derived mode (typically the title). */
  leaf?: string;
  /**
   * Fallback section label rendered as a quiet eyebrow line when the bar
   * is suppressed (a lone top-level crumb). Lets list pages keep their
   * original eyebrow while detail pages get the full navigable bar.
   */
  eyebrow?: string;
  /** Render the separating hairline + bottom spacing. */
  divider?: boolean;
  className?: string;
}) {
  const t = useT();
  const crumbs = useBreadcrumbTrail({ items, leaf });

  // Render the bar for any non-empty trail — including a lone top-level
  // crumb on a section landing page (no back chevron, just the current
  // location). Only when there's genuinely nothing to show do we fall
  // back to the plain eyebrow line (e.g. an unmapped route with no leaf).
  if (crumbs.length === 0) {
    if (!eyebrow) return null;
    return (
      <div className="mb-1.5 text-[11px] uppercase tracking-[0.16em] text-fg-muted">{eyebrow}</div>
    );
  }

  const backHref = crumbs[crumbs.length - 2]?.href;

  return (
    <nav
      aria-label="Breadcrumb"
      className={cn(
        "flex items-center gap-2 min-w-0",
        divider && "border-b border-line-subtle/60 pb-2.5 mb-3",
        className,
      )}
    >
      {backHref && (
        <Link
          href={backHref}
          aria-label={t("breadcrumb.back")}
          className="grid h-6 w-6 shrink-0 place-items-center rounded-md border border-line-subtle/70 text-fg-muted transition-colors hover:bg-bg-hover hover:text-fg"
        >
          <ChevronLeft size={14} aria-hidden />
        </Link>
      )}
      <ol className="flex items-center gap-1.5 min-w-0 text-[12.5px]">
        {crumbs.map((c, i) => {
          const isLast = i === crumbs.length - 1;
          const inner = (
            <span
              data-testid={c.testId}
              className={cn(
                // min-w-0 completes the shrink chain (nav → ol → li → here).
                // Without it this inline-flex keeps its automatic minimum
                // size, so in a narrow container the label overflowed its own
                // <li> and painted straight over the next crumb instead of
                // ellipsising — visible in the 288px workspace pane.
                "inline-flex min-w-0 items-center gap-1 truncate",
                c.mono && "font-mono",
                isLast ? "font-medium text-fg-bright" : "text-fg-dim",
              )}
            >
              {c.icon ? <span className="shrink-0 text-fg-muted">{c.icon}</span> : null}
              <span className="truncate">{c.label}</span>
            </span>
          );
          return (
            <li key={`${c.label}-${i}`} className="inline-flex min-w-0 items-center gap-1.5">
              {c.href && !isLast ? (
                // inline-flex, not inline: `min-width` is ignored on an
                // inline box, so the linked crumb refused to shrink and its
                // label overflowed onto the next crumb in a narrow container.
                <Link href={c.href} className="inline-flex min-w-0 transition-colors hover:text-fg">
                  {inner}
                </Link>
              ) : (
                inner
              )}
              {!isLast && (
                <ChevronRight size={12} className="shrink-0 text-fg-muted/60" aria-hidden />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
